// ignore-string-externalization

import React from 'react';
import { Box } from '../ui';
import { ModuleType } from '../Module/types';
import {
  getModuleBackgroundColor,
  getModuleTextColor,
  getModuleAccentColor,
  useDynamicTheme,
  DynamicGradientBackground,
  Colors,
} from '@spotify-internal/frodor-modules';

/**
 * DynamicThemeLandingPage Component
 *
 * A specialized landing page component that provides dynamic theme transitions
 * based on scroll position and module content. This component creates a smooth
 * visual experience where the background color, text color, and accent colors
 * transition dynamically as users scroll through different modules.
 *
 * Key Features:
 * - Dynamic background color transitions based on scroll position
 * - Smooth color transitions with CSS animations
 * - Integration with frodor-modules for theme management
 * - Automatic section registration for scroll-based theming
 *
 * @param allModules - Array of modules to render on the landing page
 * @param renderModule - Function to render individual modules with enhanced props
 */
export const DynamicThemeLandingPage: React.FC<{
  allModules: ModuleType[];
  renderModule: (mod: ModuleType, idx: number) => React.ReactElement;
}> = ({ allModules, renderModule }) => {
  const { registerSection, activeSection } = useDynamicTheme();

  return (
    <DynamicGradientBackground $colors={activeSection}>
      {allModules.map((mod: ModuleType, idx: number) => {
        const colorsToUse: Colors = {
          ...mod.colors,
          backgroundColor:
            activeSection?.backgroundColor || mod.colors?.backgroundColor || '',
          textColor: activeSection?.textColor || mod.colors?.textColor || '',
          accentColor: activeSection?.accentColor || mod.colors?.accentColor,
          backgroundBottomColor:
            activeSection?.backgroundBottomColor ||
            mod.colors?.backgroundBottomColor ||
            '',
        };
        return (
          <Box
            key={`${mod.id}-${idx}`}
            noPadding
            transparent
            ref={node => {
              if (node) {
                // Register this section with the dynamic theme system
                // This allows the theme to track scroll position and update colors accordingly
                registerSection(node, {
                  backgroundColor: getModuleBackgroundColor(mod as any) || '',
                  textColor: getModuleTextColor(mod as any) || '',
                  accentColor: getModuleAccentColor(mod as any) || '',
                  backgroundBottomColor: mod.colors?.backgroundBottomColor,
                });
              }
            }}
          >
            {renderModule(
              {
                ...mod,
                // Force transparent background to let parent Box handle background
                // NOTE: Modules need to forward this prop to the ColorSetWrapper
                transparent: true,
                // Override module colors with dynamic theme colors
                colors: colorsToUse,
              },
              idx,
            )}
          </Box>
        );
      })}
    </DynamicGradientBackground>
  );
};
